import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Grid,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  ErrorOutline as ErrorIcon,
  Smartphone as SmartphoneIcon,
  CameraAlt as CameraIcon,
  WhatsApp as WhatsAppIcon,
} from '@mui/icons-material';
import axios from 'axios';

const API_URL = process.env.NODE_ENV === 'development' 
  ? process.env.REACT_APP_API_URL || 'http://localhost:3001/api'
  : process.env.REACT_APP_API_URL || '/api';

const SharedQR: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [qrCode, setQrCode] = useState('');
  const [botInfo, setBotInfo] = useState<any>(null);
  const [connected, setConnected] = useState(false);
  const [expired, setExpired] = useState(false);

  useEffect(() => {
    validateToken();
    const interval = setInterval(checkQRStatus, 3000);
    return () => clearInterval(interval);
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!expired && !connected && botInfo) {
      setupWebSocket();
    }
  }, [botInfo, expired, connected]); // eslint-disable-line react-hooks/exhaustive-deps

  const validateToken = async () => {
    try {
      const response = await axios.get(`${API_URL}/public/qr-share/${token}/validate`);
      
      if (!response.data.valid) {
        setError(response.data.error || 'Link inválido');
        setExpired(true);
      } else if (response.data.connected) {
        setConnected(true);
      } else {
        setBotInfo(response.data);
      }
    } catch (err: any) {
      if (err.response?.status === 404) {
        setError('Link inválido');
      } else if (err.response?.status === 410) {
        setError('Link expirado');
        setExpired(true);
      } else {
        setError('Erro ao validar link');
      }
    } finally {
      setLoading(false);
    }
  };

  const checkQRStatus = async () => {
    if (expired || connected) return;
    
    try {
      const response = await axios.get(`${API_URL}/public/qr-share/${token}/qrcode`);
      
      if (response.data.connected) {
        setConnected(true);
        setQrCode('');
      } else if (response.data.qrCode) {
        setQrCode(response.data.qrCode);
      }
    } catch (err) {
      console.error('Erro ao verificar QR:', err);
    }
  };

  const setupWebSocket = () => {
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsHost = process.env.NODE_ENV === 'development' 
      ? 'localhost:3001' 
      : window.location.host;
    const ws = new WebSocket(`${wsProtocol}//${wsHost}/ws/qr-share?token=${token}`);

    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      
      if (data.type === 'qr_update') {
        setQrCode(data.qrCode);
      } else if (data.type === 'connection_status' && data.connected) {
        setConnected(true);
        setQrCode('');
      }
    };

    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
    };

    return () => {
      ws.close();
    };
  };

  const steps = [
    {
      label: 'Abra o WhatsApp no seu celular',
      description: 'Certifique-se de que você está usando o WhatsApp no dispositivo que deseja conectar.',
      icon: <SmartphoneIcon />,
    },
    {
      label: 'Acesse as configurações',
      description: 'No WhatsApp, toque nos três pontos (Android) ou Configurações (iPhone) e selecione "Dispositivos conectados".',
      icon: <WhatsAppIcon />,
    },
    {
      label: 'Conectar um dispositivo',
      description: 'Toque em "Conectar um dispositivo" e aponte a câmera para o QR Code abaixo.',
      icon: <CameraIcon />,
    },
  ];

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 8 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <CircularProgress />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Validando link...
          </Typography>
        </Paper>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 8 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <ErrorIcon sx={{ fontSize: 60, color: 'error.main', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            {error}
          </Typography>
          {expired && (
            <Typography variant="body1" color="textSecondary">
              Este link de compartilhamento expirou. Solicite um novo link ao administrador do bot.
            </Typography>
          )}
        </Paper>
      </Container>
    );
  }

  if (connected) {
    return (
      <Container maxWidth="md" sx={{ mt: 8 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <CheckCircleIcon sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
          <Typography variant="h4" gutterBottom>
            WhatsApp Conectado com Sucesso!
          </Typography>
          <Typography variant="body1" color="textSecondary" sx={{ mt: 2 }}>
            O WhatsApp foi conectado ao bot "{botInfo?.botName || 'Bot'}".
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            Você já pode fechar esta página.
          </Typography>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" align="center" gutterBottom>
        Conectar WhatsApp ao Bot
      </Typography>
      
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Informações do Bot
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="textSecondary">
                  Nome do Bot:
                </Typography>
                <Typography variant="body1">
                  {botInfo?.botName || 'Carregando...'}
                </Typography>
              </Box>
              {botInfo?.botDescription && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Descrição:
                  </Typography>
                  <Typography variant="body1">
                    {botInfo.botDescription}
                  </Typography>
                </Box>
              )}
              {botInfo?.metadata?.ownerName && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Criado por:
                  </Typography>
                  <Typography variant="body1">
                    {botInfo.metadata.ownerName}
                  </Typography>
                </Box>
              )}
              <Alert severity="info" sx={{ mt: 2 }}>
                Este link expira em 10 minutos após ser gerado
              </Alert>
            </CardContent>
          </Card>

          <Card sx={{ mt: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Como Conectar
              </Typography>
              <Stepper orientation="vertical">
                {steps.map((step, index) => (
                  <Step key={index} active={true}>
                    <StepLabel icon={step.icon}>
                      {step.label}
                    </StepLabel>
                    <StepContent>
                      <Typography variant="body2" color="textSecondary">
                        {step.description}
                      </Typography>
                    </StepContent>
                  </Step>
                ))}
              </Stepper>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" align="center" gutterBottom>
                QR Code
              </Typography>
              {!qrCode ? (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                  <CircularProgress />
                  <Typography variant="body1" sx={{ mt: 2 }}>
                    Gerando QR Code...
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ textAlign: 'center' }}>
                  <img 
                    src={qrCode} 
                    alt="QR Code" 
                    style={{ 
                      maxWidth: '100%', 
                      width: '300px',
                      height: 'auto'
                    }} 
                  />
                  <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                    Escaneie este código com o WhatsApp
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};

export default SharedQR;